import { notFound } from '@tanstack/react-router'

/**
 * `src/pages/dev/*`'s getServerSideProps NODE_ENV guard, as a beforeLoad.
 *
 * Both halves are deliberate. `import.meta.env.DEV` is Vite's own flag, but it
 * is derived from the AMBIENT NODE_ENV, and `apps/app/.env.local` sets
 * NODE_ENV=development — so on a dev machine a production `vite build`
 * compiles that half away and the guard becomes an empty function. The
 * `process.env.NODE_ENV` half is substituted by vite.config.mts `define` from
 * the build MODE ('production' for `vite build`), which no local env file can
 * flip. Development needs both to say development.
 */
export function assertDevelopmentBuild(): void {
  if (!import.meta.env.DEV) throw notFound()
  if (process.env.NODE_ENV !== 'development') throw notFound()
}

/**
 * The production stand-in for a `/dev/*` preview component.
 *
 * A `beforeLoad` guard alone cannot keep the preview code OUT of a production
 * bundle: `autoCodeSplitting` turns every `component` into a lazy import that
 * Rollup must emit, so all thirteen preview chunks shipped — unreachable, but
 * downloadable by anyone who guessed the hashed asset name, sourcemaps and all.
 *
 * The fix has to live at each route's own call site, because only a literal
 * `import.meta.env.DEV ? lazy(() => import(…)) : DevPreviewUnavailable` at
 * module scope gives Rollup a constant condition it can fold: in a production
 * build the condition is `false`, the whole true-branch (dynamic import
 * included) is dead code, and the preview chunk is never emitted. Wrapping the
 * loader in a helper would defeat this — the arrow function would be an
 * argument Rollup cannot prove unused.
 *
 * This component is never rendered: the guard above throws notFound() first.
 * It exists so the false branch of that ternary is a valid component type.
 *
 * The route tree is identical in both modes (the route files are always
 * generated), so `src/routeTree.gen.ts` stays byte-stable across a dev build
 * and a production build. Only the chunks differ.
 */
export function DevPreviewUnavailable(): null {
  return null
}
